import React from "react";
import { useTranslation } from "react-i18next";

import AvatarUploader from "@/components/avatar-upload";
import FileUploader from "@/components/file-uploader";
import { isApiError } from "@/queries/api/apiUtils";
import {
  useUploadAvatarMutation,
  useUploadResumeMutation,
} from "@/queries/hooks/fileUploadHooks";
import { notify } from "@/utils";
import { Logger } from "@/utils/logger";

import { useWorkerOnboarding } from "../-worker-onboarding-context";

interface Props {
  onContinue?: (handler: () => Promise<boolean>) => void;
}

const PHOTO_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const RESUME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const MAX_BYTES = 5 * 1024 * 1024;

// Pull the trailing path segment off the stored URL so the user sees the file
// they uploaded instead of a long S3 URL.
function fileNameFromUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const path = new URL(url).pathname;
    const last = path.split("/").filter(Boolean).pop();
    if (!last) return undefined;
    const decoded = decodeURIComponent(last);
    // Strip the UUID prefix FileStorageService prepends ("<uuid>_originalName.ext").
    const underscoreIdx = decoded.indexOf("_");
    if (underscoreIdx > 0 && underscoreIdx < decoded.length - 1) {
      return decoded.slice(underscoreIdx + 1);
    }
    return decoded;
  } catch {
    return undefined;
  }
}

export function WorkerResumePhotoStep({ onContinue }: Props) {
  const { t } = useTranslation("onboarding");
  const { data, setPhotoUrl, setResumeUrl, saveDraft } = useWorkerOnboarding();
  const uploadAvatar = useUploadAvatarMutation();
  const uploadResume = useUploadResumeMutation();
  const [photoBusy, setPhotoBusy] = React.useState(false);
  const [resumeBusy, setResumeBusy] = React.useState(false);

  React.useEffect(() => {
    if (!onContinue) return;
    onContinue(async () => {
      try {
        await saveDraft();
        return true;
      } catch (e) {
        Logger.error("worker onboarding save failed at uploads", {
          error: e instanceof Error ? e.message : String(e),
        });
        notify.error(
          e instanceof Error ? e.message : t("workerResumePhoto.saveFailed"),
        );
        return false;
      }
    });
  }, [onContinue, saveDraft, t]);

  const handlePhoto = async (file: File | null) => {
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      notify.error(t("workerResumePhoto.photoTypeError"));
      return;
    }
    if (file.size > MAX_BYTES) {
      notify.error(t("workerResumePhoto.photoTooLarge"));
      return;
    }
    setPhotoBusy(true);
    try {
      const res = await uploadAvatar.mutateAsync(file);
      const url = res.profile.avatar_url ?? res.profile.avatar;
      setPhotoUrl(url ?? undefined);
      notify.success(t("workerResumePhoto.photoUploaded"));
    } catch (e) {
      Logger.error("photo upload failed", {
        error: e instanceof Error ? e.message : String(e),
        status: isApiError(e) ? e.status : undefined,
      });
      if (isApiError(e) && e.status === 413) {
        notify.error(t("workerResumePhoto.photoTooLargeServer"));
      } else {
        notify.error(t("workerResumePhoto.photoUploadFailed"));
      }
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleResume = async (file: File) => {
    if (!RESUME_TYPES.includes(file.type)) {
      notify.error(t("workerResumePhoto.resumeTypeError"));
      return;
    }
    if (file.size > MAX_BYTES) {
      notify.error(t("workerResumePhoto.resumeTooLarge"));
      return;
    }
    setResumeBusy(true);
    try {
      const res = await uploadResume.mutateAsync(file);
      const url = res.profile.resume_signed_url ?? res.profile.resume_url;
      setResumeUrl(url ?? undefined);
      notify.success(t("workerResumePhoto.resumeUploaded"));
    } catch (e) {
      Logger.error("resume upload failed", {
        error: e instanceof Error ? e.message : String(e),
        status: isApiError(e) ? e.status : undefined,
      });
      if (isApiError(e) && e.status === 413) {
        notify.error(t("workerResumePhoto.resumeTooLargeServer"));
      } else {
        notify.error(t("workerResumePhoto.resumeUploadFailed"));
      }
    } finally {
      setResumeBusy(false);
    }
  };

  const resumeFileName = fileNameFromUrl(data.resumeUrl);
  const resumeUploaded = Boolean(data.resumeUrl);

  return (
    <section className="w-full flex flex-col gap-8">
      <div>
        <h2 className="heading-1">{t("workerResumePhoto.title")}</h2>
        <p className="body-small text-gray-600 mt-2">
          {t("workerResumePhoto.description")}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between">
          <span className="font-semibold">{t("workerResumePhoto.profilePhoto")}</span>
          <span className="body-small text-gray-500">{t("workerResumePhoto.optional")}</span>
        </div>
        <AvatarUploader
          size="lg"
          value={data.photoUrl ?? null}
          onChange={(file) => void handlePhoto(file)}
          loading={photoBusy}
          accept={PHOTO_TYPES.join(",")}
          note={t("workerResumePhoto.photoNote")}
        />
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between">
          <span className="font-semibold">{t("workerResumePhoto.resume")}</span>
          <span className="body-small text-gray-500">{t("workerResumePhoto.optional")}</span>
        </div>
        {resumeUploaded ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3 rounded-md border border-garnet/30 bg-fog px-4 py-3">
              <div className="flex flex-col min-w-0">
                <span className="body-small text-gray-500">
                  {t("workerResumePhoto.currentResume")}
                </span>
                <a
                  href={data.resumeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-garnet underline truncate"
                  title={resumeFileName ?? t("workerResumePhoto.resumeFallbackTitle")}
                >
                  {resumeFileName ?? t("workerResumePhoto.viewResume")}
                </a>
              </div>
              <label className="shrink-0 cursor-pointer text-garnet underline body-small">
                {resumeBusy
                  ? t("workerResumePhoto.uploadingShort")
                  : t("workerResumePhoto.replace")}
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="sr-only"
                  disabled={resumeBusy}
                  onChange={(e) => {
                    const f = e.currentTarget.files?.[0];
                    if (f) void handleResume(f);
                    e.currentTarget.value = "";
                  }}
                />
              </label>
            </div>
          </div>
        ) : (
          <FileUploader
            variant="large"
            label={t("workerResumePhoto.uploadResumeLabel")}
            description={t("workerResumePhoto.uploadResumeDescription")}
            accept={[".pdf", ".doc", ".docx"]}
            loading={resumeBusy}
            disabled={resumeBusy}
            onFileChange={(e) => {
              const f = e.currentTarget.files?.[0];
              if (f) void handleResume(f);
              e.currentTarget.value = "";
            }}
          />
        )}
      </div>
    </section>
  );
}
